import { useEffect, useMemo, useState } from 'react';
import '../../common/Mypage.scss';
import MypageMenu from '../../common/MypageMenu';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import MainURL from '../../../../MainURL';
import { useAtom } from 'jotai';
import { loginStateAtom, userDataAtom } from '../../../../AtomStore';
import { getUserTypeLabel } from '../../../login/userTypeUtils';
import { fetchScrapList, type ScrapListItem } from '../../common/scrapApi';
import type { PaymentListItem } from '../../common/ServicePaymentList';

type FilterType = 'all' | 'recruit' | 'retreat_place' | 'retreat_casting';

const SCRAP_FILTERS: { id: FilterType; label: string }[] = [
  { id: 'all', label: '전체' },
  { id: 'recruit', label: '구인구직' },
  { id: 'retreat_place', label: '수련회 장소' },
  { id: 'retreat_casting', label: '수련회 강사' },
];

function scrapTypeLabel(targetType: string) {
  if (targetType === 'recruit') return '구인구직';
  if (targetType === 'retreat_place') return '수련회 장소';
  if (targetType === 'retreat_casting') return '수련회 강사';
  return targetType;
}

function scrapTagClass(targetType: string) {
  if (targetType === 'recruit') return 'mypage-tag--green';
  return 'mypage-tag--gold';
}

function fallbackScrapPath(item: ScrapListItem) {
  if (item.targetType === 'recruit') {
    if (item.tableType === 'church') return `/recruit/church/${item.targetId}`;
    if (item.tableType === 'institute') return `/recruit/institute/${item.targetId}`;
    if (item.tableType === 'overseas') return `/recruit/overseas/${item.targetId}`;
    return `/recruit/minister/${item.targetId}`;
  }
  if (item.targetType === 'retreat_place') return `/retreat/place/detail?id=${item.targetId}`;
  return `/retreat/casting/detail?id=${item.targetId}`;
}

function formatScrapDate(value: string) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return { date: '-', time: '' };
  return {
    date: d.toLocaleDateString('ko-KR', { year: 'numeric', month: 'numeric', day: 'numeric' }),
    time: d.toLocaleTimeString('ko-KR', { hour: 'numeric', minute: '2-digit' }),
  };
}

function daysAgoLabel(value: string | undefined) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const diff = Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
  if (diff <= 0) return '오늘 수정';
  return `${diff}일 전 수정`;
}

interface ProfileProps {
  grade: string;
  userAccount: string;
  userNickName: string;
  userDetail: string;
  userURL: string;
  user_type?: string;
}

export default function Profile() {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useAtom(loginStateAtom);
  const [userData, setUserData] = useAtom(userDataAtom);

  const [currentTab, setCurrentTab] = useState(1);
  const [refresh, setRefresh] = useState(false);
  const [scrapFilter, setScrapFilter] = useState<FilterType>('all');

  const [userProfile, setUserProfile] = useState<ProfileProps>();
  const [userAccount, setUserAccount] = useState('');
  const [userNickName, setUserNickName] = useState('');
  const [userDetail, setUserDetail] = useState('');
  const [userURL, setUserURL] = useState('');
  const [passwordCurrent, setPasswordCurrent] = useState('');
  const [passwordChange, setPasswordChange] = useState('');

  const [scrapList, setScrapList] = useState<ScrapListItem[]>([]);
  const [postCount, setPostCount] = useState(0);
  const [resumeCount, setResumeCount] = useState(0);
  const [resumeUpdatedAt, setResumeUpdatedAt] = useState('');
  const [serviceRows, setServiceRows] = useState<PaymentListItem[]>([]);

  const [isViewPasswdChange, setIsViewPasswdChange] = useState(false);
  const [isViewDeleteAccount, setIsViewDeleteAccount] = useState(false);
  const [agreeDeleteAccount, setAgreeDeleteAccount] = useState(false);

  const fetchPosts = async () => {
    if (!userData.userAccount) return;
    const res = await axios.get(`${MainURL}/mypage/getprofile/${userData.userAccount}`);
    if (res.data?.[0]) {
      setUserProfile(res.data[0]);
      setUserAccount(res.data[0].userAccount);
      setUserNickName(res.data[0].userNickName);
      setUserDetail(res.data[0].userDetail);
      setUserURL(res.data[0].userURL);
    }
  };

  const fetchDashboard = async () => {
    if (!userData.userAccount) return;
    const account = userData.userAccount;

    const [scraps, postsRes, resumeRes, paymentRes] = await Promise.all([
      fetchScrapList(account),
      axios.get(`${MainURL}/mypage/getuserposts/${account}`).catch(() => ({ data: [] })),
      axios.get(`${MainURL}/resume/getresumelist/${account}`).catch(() => ({ data: { resumeList: [] } })),
      axios.get(`${MainURL}/payment/my/list`, { params: { userAccount: account, limit: 100 } }).catch(() => ({ data: { rows: [] } })),
    ]);

    setScrapList(scraps);
    setPostCount(Array.isArray(postsRes.data) ? postsRes.data.length : 0);

    const resumes = resumeRes.data?.resumeList;
    if (Array.isArray(resumes)) {
      setResumeCount(resumes.length);
      const latest = resumes.reduce<string | undefined>((acc, item: { updatedAt?: string }) => {
        if (!item.updatedAt) return acc;
        if (!acc || new Date(item.updatedAt) > new Date(acc)) return item.updatedAt;
        return acc;
      }, undefined);
      setResumeUpdatedAt(latest || '');
    } else {
      setResumeCount(0);
      setResumeUpdatedAt('');
    }

    if (paymentRes.data?.ok && Array.isArray(paymentRes.data.rows)) {
      setServiceRows(paymentRes.data.rows);
    } else {
      setServiceRows([]);
    }
  };

  useEffect(() => {
    void fetchPosts();
    void fetchDashboard();
  }, [refresh, userData.userAccount]);

  const filteredScraps = useMemo(() => {
    const list = scrapFilter === 'all' ? scrapList : scrapList.filter((item) => item.targetType === scrapFilter);
    return list.slice(0, 2);
  }, [scrapFilter, scrapList]);

  const recentScrapDelta = useMemo(() => {
    const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return scrapList.filter((item) => new Date(item.createdAt).getTime() >= weekAgo).length;
  }, [scrapList]);

  const displayName = userProfile?.userNickName || userData.userNickName || '회원';
  const avatarChar = displayName.charAt(0) || '회';
  const userTypeLabel = getUserTypeLabel(userProfile?.user_type || userData.userType) || '-';

  const handleRevise = async () => {
    await axios
      .post(`${MainURL}/mypage/changeprofile`, {
        userAccount,
        userNickName,
        userDetail,
      })
      .then((res) => {
        if (res.data) {
          setUserData((prev: typeof userData) => ({
            ...prev,
            userNickName,
            userDetail,
          }));
          setRefresh(!refresh);
          alert('수정되었습니다.');
          setCurrentTab(1);
        }
      })
      .catch(() => {
        alert('다시 시도해주세요.');
      });
  };

  const handlePasswdChange = async () => {
    await axios
      .post(`${MainURL}/mypage/profilechangepassword`, {
        userAccount,
        userNickName,
        passwordCurrent,
        passwordChange,
      })
      .then((res) => {
        if (res.data === true) {
          setRefresh(!refresh);
          alert('변경되었습니다.');
          setIsViewPasswdChange(false);
        } else {
          alert(res.data);
        }
      })
      .catch(() => {
        alert('다시 시도해주세요.');
      });
  };

  const handleDeleteAccount = async () => {
    await axios
      .post(`${MainURL}/mypage/deleteaccount`, { userAccount, verifyToken: localStorage.getItem('refreshToken') || '' })
      .then((res) => {
        if (res.data === true) {
          setRefresh(!refresh);
          alert('탈퇴되었습니다.');
          setIsLogin(false);
          setUserData({
            userAccount: '',
            userNickName: '',
            userSort: '',
            userDetail: '',
            userType: '',
            grade: '',
            authInstitution: '',
            authChurch: '',
            authDepartment: '',
            authGroup: '',
          });
          sessionStorage.clear();
          navigate('/');
          window.location.reload();
        } else {
          alert(res.data);
        }
      })
      .catch((error) => {
        // 401: 로그인 토큰이 없거나 만료됨 — 본인 확인 후에만 탈퇴할 수 있다
        alert(error?.response?.status === 401 ? '로그인이 만료되었습니다. 다시 로그인한 뒤 탈퇴해 주세요.' : '다시 시도해주세요.');
      });
  };

  return (
    <div className="mypage">
      <div className="inner">
        <MypageMenu />
        <div className="subpage__main">
          {currentTab === 1 ? (
            <>
              <div className="mypage-panel mypage-hero-card">
                <div className="mypage-hero-top">
                  <div>
                    <div className="mypage-profile-group">
                      <div className="mypage-avatar">{avatarChar}</div>
                      <div>
                        <div className="mypage-eyebrow">MY PAGE DASHBOARD</div>
                        <h1 className="mypage-hero-title">{displayName}님의 사역 현황을 한눈에 확인하세요</h1>
                        <p className="mypage-hero-desc">
                          프로필, 스크랩, 공고글, 이용 중인 서비스를 하나의 화면에서 빠르게 확인할 수 있도록
                          마이페이지를 대시보드형으로 구성했습니다.
                        </p>
                        <div className="mypage-user-meta">
                          <span className="mypage-chip">계정 · {userProfile?.userAccount || userData.userAccount}</span>
                          <span className="mypage-chip">구분 · {userTypeLabel}</span>
                          <span className="mypage-chip">가입경로 · {userProfile?.userURL || userURL || '-'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mypage-stats-grid">
                  <div className="mypage-stat-card">
                    <div className="mypage-stat-label">스크랩</div>
                    <div className="mypage-stat-value">{scrapList.length}</div>
                    <div className="mypage-stat-foot">
                      {recentScrapDelta > 0 ? `최근 7일 +${recentScrapDelta}건 저장` : '저장한 스크랩이 없습니다'}
                    </div>
                  </div>
                  <div className="mypage-stat-card">
                    <div className="mypage-stat-label">공고글</div>
                    <div className="mypage-stat-value">{postCount}</div>
                    <div className="mypage-stat-foot">등록한 공고글 {postCount}건</div>
                  </div>
                  <div className="mypage-stat-card">
                    <div className="mypage-stat-label">서비스</div>
                    <div className="mypage-stat-value">{serviceRows.length}</div>
                    <div className="mypage-stat-foot">
                      {serviceRows.length > 0 ? '이용 중인 서비스 확인' : '이용 중인 서비스 없음'}
                    </div>
                  </div>
                  <div className="mypage-stat-card">
                    <div className="mypage-stat-label">이력서</div>
                    <div className="mypage-stat-value">{resumeCount}</div>
                    <div className="mypage-stat-foot">
                      {resumeUpdatedAt ? `최근 ${daysAgoLabel(resumeUpdatedAt)}` : '작성한 이력서 없음'}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mypage-panel mypage-mini-panel">
                <div className="mypage-section-head">
                  <h4 className="mypage-section-title">프로필 요약</h4>
                  <button type="button" className="mypage-btn mypage-btn--primary" onClick={() => setCurrentTab(2)}>
                    프로필 수정하기
                  </button>
                </div>
                <div className="mypage-profile-list">
                  <div className="mypage-profile-row">
                    <strong>닉네임</strong>
                    <span>{userProfile?.userNickName || '-'}</span>
                  </div>
                  <div className="mypage-profile-row">
                    <strong>계정</strong>
                    <span>{userProfile?.userAccount || '-'}</span>
                  </div>
                  <div className="mypage-profile-row">
                    <strong>구분</strong>
                    <span>{userTypeLabel}</span>
                  </div>
                  <div className="mypage-profile-row">
                    <strong>상세</strong>
                    <span>{userProfile?.userDetail || '-'}</span>
                  </div>
                  <div className="mypage-profile-row">
                    <strong>가입경로</strong>
                    <span>{userProfile?.userURL || '-'}</span>
                  </div>
                </div>
                <div className="mypage-notice-box">
                  프로필 정보는 서비스 노출 신뢰도와 연결됩니다. 교회명, 역할, 소개 문구를 최신 상태로 유지해 주세요.
                </div>
              </div>

              <div className="mypage-panel mypage-section-card">
                <div className="mypage-section-head">
                  <div>
                    <h3 className="mypage-section-title">최근 스크랩</h3>
                    <p className="mypage-section-copy">
                      자주 보는 항목을 빠르게 다시 확인할 수 있도록 최근 저장한 스크랩을 요약했습니다.
                    </p>
                  </div>
                  <div className="mypage-filter-row">
                    {SCRAP_FILTERS.map((tab) => (
                      <button
                        key={tab.id}
                        type="button"
                        className={`mypage-filter-chip ${scrapFilter === tab.id ? 'is-active' : ''}`}
                        onClick={() => setScrapFilter(tab.id)}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mypage-scrap-list">
                  {filteredScraps.length > 0 ? (
                    filteredScraps.map((item) => {
                      const { date, time } = formatScrapDate(item.createdAt);
                      return (
                        <article
                          key={item.id}
                          className="mypage-scrap-item"
                          onClick={() => {
                            navigate(item.linkPath || fallbackScrapPath(item));
                            window.scrollTo(0, 0);
                          }}
                        >
                          <div className="mypage-row-between">
                            <div className="mypage-tag-row">
                              <span className="mypage-tag mypage-tag--sky">{item.targetType}</span>
                              <span className={`mypage-tag ${scrapTagClass(item.targetType)}`}>
                                {scrapTypeLabel(item.targetType)}
                              </span>
                            </div>
                            <span className="mypage-meta">
                              <span>{date}</span>
                              {time ? <span>{time}</span> : null}
                            </span>
                          </div>
                          <h4 className="mypage-scrap-title">{item.title || '(제목 없음)'}</h4>
                          <div className="mypage-meta">
                            {item.subtitle ? (
                              <span>
                                <strong>부제</strong> {item.subtitle}
                              </span>
                            ) : null}
                            {item.meta ? (
                              <span>
                                <strong>정보</strong> {item.meta}
                              </span>
                            ) : null}
                          </div>
                        </article>
                      );
                    })
                  ) : (
                    <div className="mypage-notice-box">스크랩한 항목이 없습니다.</div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="mypage-panel mypage-section-card">
              <div className="mypage-page-header">
                <h2 className="mypage-section-title">프로필 수정</h2>
                <button type="button" className="mypage-btn mypage-btn--outline" onClick={() => setCurrentTab(1)}>
                  대시보드로 돌아가기
                </button>
              </div>

              <div className="subpage__main__content" style={{ marginTop: 20 }}>
                <div className="main__content">
                  <div className="textarea">
                    <div className="textrow">
                      <h3>닉네임</h3>
                      <input
                        value={userNickName}
                        className="profileinputdefault"
                        type="text"
                        onChange={(e) => setUserNickName(e.target.value)}
                      />
                    </div>
                    <div className="textrow">
                      <h3>상세</h3>
                      <input
                        value={userDetail}
                        className="profileinputdefault"
                        type="text"
                        onChange={(e) => setUserDetail(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <div className="divider" />

                {userURL === 'email' ? (
                  <div
                    className="reviseBtn"
                    onClick={() => setIsViewPasswdChange(true)}
                    role="button"
                    tabIndex={0}
                  >
                    <p>비밀번호 변경</p>
                  </div>
                ) : null}

                {isViewPasswdChange ? (
                  <>
                    <div className="main__content">
                      <div className="textarea">
                        <div className="textrow">
                          <h3>현재 비번</h3>
                          <input
                            value={passwordCurrent}
                            className="profileinputdefault"
                            type="password"
                            onChange={(e) => setPasswordCurrent(e.target.value)}
                          />
                        </div>
                        <div className="textrow">
                          <h3>변경할 비번</h3>
                          <input
                            value={passwordChange}
                            className="profileinputdefault"
                            type="password"
                            onChange={(e) => setPasswordChange(e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="reviseBtn" onClick={handlePasswdChange} role="button" tabIndex={0}>
                      <p>변경완료</p>
                    </div>
                  </>
                ) : null}

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  <div className="reviseBtn reviseBtn--outline" onClick={() => setCurrentTab(1)} role="button" tabIndex={0}>
                    <p>이전</p>
                  </div>
                  <div className="reviseBtn" onClick={handleRevise} role="button" tabIndex={0}>
                    <p>수정완료</p>
                  </div>
                </div>

                {!isViewDeleteAccount ? (
                  <div className="accountDeleteBtn">
                    <div className="deleteBtnBox" onClick={() => setIsViewDeleteAccount(true)} role="button" tabIndex={0}>
                      <p className="deleteBtn">회원탈퇴</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="deleteAccountBox">
                      <div className="deleteAccountCover">
                        <h1>탈퇴시 모든 정보가 사라지며, 복구할 수 없습니다.</h1>
                        <h2>* 유의 사항 안내</h2>
                        <div>
                          <p>1. 회원 탈퇴 시, 즉시 탈퇴 처리되며, 서비스 이용이 불가합니다.</p>
                        </div>
                        <div>
                          <p>2. 기존에 작성한 게시물 및 댓글은 자동으로 삭제되지 않습니다.</p>
                          <p>또한 탈퇴 이후에는 작성자 본인을 확인할 수 없으므로,</p>
                          <p>모든 게시물의 권한은 전적으로 운영진에게 위임됩니다.</p>
                        </div>
                        <div>
                          <p>3. 회원 정보는 탈퇴 즉시 삭제되지만, 부정 이용 거래 방지 및 전자상거래법 등 관련 법령에 따라,</p>
                          <p>보관이 필요할 경우 해당 기간 동안 회원 정보가 보관 될 수 있습니다.</p>
                        </div>
                      </div>
                    </div>
                    <div className="deletecheckInputCover">
                      <div className="deletecheckInput">
                        <input
                          className="deleteinput"
                          type="checkbox"
                          checked={agreeDeleteAccount}
                          onChange={() => setAgreeDeleteAccount(!agreeDeleteAccount)}
                        />
                        <h5>유의사항을 확인했습니다.</h5>
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      <div
                        className="reviseBtn reviseBtn--outline"
                        onClick={() => setIsViewDeleteAccount(false)}
                        role="button"
                        tabIndex={0}
                      >
                        <p>취소</p>
                      </div>
                      <div
                        className={`reviseBtn ${agreeDeleteAccount ? 'reviseBtn--danger' : ''}`}
                        onClick={() => {
                          agreeDeleteAccount ? handleDeleteAccount() : alert('유의사항을 확인에 체크해주세요!');
                        }}
                        role="button"
                        tabIndex={0}
                      >
                        <p>탈퇴하기</p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
